function isRecord(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
}
function isFiniteNumber(value) {
    return typeof value === 'number' && Number.isFinite(value);
}
/**
 * The canvas floor the blueprint schema declares
 * (`BMS-spec/schemas/floor-plan.v1.schema.json` `canvas.width.minimum` 320,
 * `canvas.height.minimum` 240). A definition below it is not a plan the API
 * would have accepted, so the dashboard does not render one.
 */
const MIN_CANVAS_WIDTH = 320;
const MIN_CANVAS_HEIGHT = 240;
/**
 * Corner containment is decided on the rotated rectangle, and rotation makes
 * the arithmetic inexact (`cos(90 deg)` is 6.1e-17, not 0). The tolerance is
 * far below one design-space pixel, so it only forgives that noise.
 */
const CORNER_EPSILON = 1e-6;
/**
 * The four corners of a placement after its `rotation`, in canvas coordinates.
 * CSS rotates about the element's centre and that is exactly what the
 * floor-plan view renders (`transform: rotate(<rotation>deg)`), so the centre
 * is the pivot here too.
 */
function rotatedCorners(placement) {
    const centreX = placement.x + placement.width / 2;
    const centreY = placement.y + placement.height / 2;
    const radians = ((placement.rotation ?? 0) * Math.PI) / 180;
    const cos = Math.cos(radians);
    const sin = Math.sin(radians);
    const halfWidth = placement.width / 2;
    const halfHeight = placement.height / 2;
    return [
        [-halfWidth, -halfHeight],
        [halfWidth, -halfHeight],
        [halfWidth, halfHeight],
        [-halfWidth, halfHeight],
    ].map(([dx, dy]) => ({
        x: centreX + dx * cos - dy * sin,
        y: centreY + dx * sin + dy * cos,
    }));
}
export function floorScopeKey(buildingCode, floorCode) {
    return `${buildingCode ?? ''}\u0000${floorCode}`;
}
export function roomMatchesFloorPlanTarget(room, target) {
    return (room.buildingCode ?? undefined) === target.building_code && room.floorCode === target.floor_code;
}
export function isFloorPlanBlueprint(value) {
    if (!isRecord(value) || value.schema !== 'floor/v1')
        return false;
    if (!isRecord(value.meta) || typeof value.meta.name !== 'string' || value.meta.name.length === 0)
        return false;
    if (!isRecord(value.target) || value.target.floor_code === undefined)
        return false;
    if (typeof value.target.floor_code !== 'string' || value.target.floor_code.length === 0)
        return false;
    if (value.target.building_code !== undefined && typeof value.target.building_code !== 'string')
        return false;
    if (!Array.isArray(value.target.space_categories) || value.target.space_categories.length !== 1 || value.target.space_categories[0] !== 'hospitality')
        return false;
    if (!isRecord(value.canvas) || !isFiniteNumber(value.canvas.width) || !isFiniteNumber(value.canvas.height))
        return false;
    if (value.canvas.width < MIN_CANVAS_WIDTH || value.canvas.height < MIN_CANVAS_HEIGHT)
        return false;
    if (!Array.isArray(value.rooms))
        return false;
    return true;
}
export function isUsableFloorPlanPlacement(placement, canvas) {
    if (!isRecord(placement) || typeof placement.room_code !== 'string' || placement.room_code.length === 0)
        return false;
    const values = [placement.x, placement.y, placement.width, placement.height];
    if (!values.every(isFiniteNumber))
        return false;
    const { x, y, width, height } = placement;
    if (x < 0 || y < 0 || width <= 0 || height <= 0)
        return false;
    if (placement.rotation !== undefined && !isFiniteNumber(placement.rotation))
        return false;
    // GRMS spec section 15.5 rejects "placements whose rotated rectangle has any
    // corner outside 0 <= x <= canvas.width and 0 <= y <= canvas.height". The
    // axis-aligned box this used to test is the same thing only at rotation 0;
    // at any other angle it both accepted overhanging plates and rejected
    // legal ones.
    return rotatedCorners(placement).every((corner) => (corner.x >= -CORNER_EPSILON
        && corner.y >= -CORNER_EPSILON
        && corner.x <= canvas.width + CORNER_EPSILON
        && corner.y <= canvas.height + CORNER_EPSILON));
}
/** Select one deterministic plan for each exact building/floor scope. */
export function selectFloorPlanLayouts(layouts) {
    const byScope = new Map();
    for (const layout of layouts) {
        if (layout.templateType !== 'floor' || !isFloorPlanBlueprint(layout.definition))
            continue;
        const blueprint = layout.definition;
        const key = floorScopeKey(blueprint.target.building_code, blueprint.target.floor_code);
        const candidates = byScope.get(key) ?? [];
        candidates.push({ layout, blueprint });
        byScope.set(key, candidates);
    }
    return Array.from(byScope.values())
        .map((candidates) => candidates.sort((a, b) => {
        if (a.layout.isDefault !== b.layout.isDefault)
            return a.layout.isDefault ? -1 : 1;
        return a.layout.templateCode.localeCompare(b.layout.templateCode);
    })[0])
        .sort((a, b) => {
        const aKey = floorScopeKey(a.blueprint.target.building_code, a.blueprint.target.floor_code);
        const bKey = floorScopeKey(b.blueprint.target.building_code, b.blueprint.target.floor_code);
        return aKey.localeCompare(bKey);
    });
}
/** Resolve safe, in-scope placements and return every remaining room for grid fallback. */
export function resolveFloorPlanView(layouts, rooms) {
    const roomsByCode = new Map(rooms.map((room) => [room.roomCode, room]));
    const placedRoomIds = new Set();
    const plans = selectFloorPlanLayouts(layouts).map(({ layout, blueprint }) => {
        const positionedRooms = [];
        const seenCodes = new Set();
        for (const placement of blueprint.rooms) {
            if (!isUsableFloorPlanPlacement(placement, blueprint.canvas) || seenCodes.has(placement.room_code))
                continue;
            seenCodes.add(placement.room_code);
            const room = roomsByCode.get(placement.room_code);
            if (!room || !roomMatchesFloorPlanTarget(room, blueprint.target))
                continue;
            positionedRooms.push({ room, placement });
            placedRoomIds.add(room.id);
        }
        return {
            layout,
            blueprint,
            scopeKey: floorScopeKey(blueprint.target.building_code, blueprint.target.floor_code),
            positionedRooms,
        };
    });
    return {
        plans,
        unplacedRooms: rooms.filter((room) => !placedRoomIds.has(room.id)),
    };
}
/** Shared click hand-off keeps floor-plan overlays on the dashboard's existing room navigation. */
export function activateFloorPlanRoom(room, onRoomClick) {
    onRoomClick(room);
}
//# sourceMappingURL=floorPlan.js.map